import { useLingui } from "@lingui/react/macro";
import { FormInput, FormSelect } from "@offstreetparking/design-system/forms";
import { Heading, Text } from "@offstreetparking/design-system/typography";
import { isAdditionalInfoEmailLabel, isValidEmail } from "@offstreetparking/domain/value/email";
import { useMemo } from "react";
import { Form } from "../../../forms/Form";
import { useZod } from "../../../providers/TranslationsProvider";
import type { AdditionalInfoFormData } from "../../PermitContextProvider/types";
import type { PermitTypeDto } from "../../types";
export const AdditionalInfoTypeEnum = {
	Input: 0,
	Dropdown: 1,
} as const;
type AdditionalInfoFormProps = {
	formId: string;
	additionalInfo: PermitTypeDto["wizardSettings"]["additionalInformation"]["fields"];
	initialValues: Partial<AdditionalInfoFormData>;
	onSubmit: (data: AdditionalInfoFormData) => void;
};

export const AdditionalInfoForm = ({ formId, additionalInfo, initialValues, onSubmit }: AdditionalInfoFormProps) => {
	const z = useZod();
	const { t } = useLingui();

	const schema = useMemo(() => {
		const baseSchema = z.object({
			fields: z.array(
				z.discriminatedUnion("isRequired", [
					z.object({
						permitFieldId: z.number(),
						value: z.string(),
						label: z.string().optional(),
						isRequired: z.literal(true),
					}),
					z.object({
						permitFieldId: z.number(),
						value: z.string().optional(),
						label: z.string().optional(),
						isRequired: z.literal(false),
					}),
				]),
			),
		});
		return baseSchema.superRefine((data, ctx) => {
			data.fields.forEach((field, index) => {
				if (field.isRequired && (!field.value || field.value.trim().length === 0)) {
					ctx.addIssue({
						code: "custom",
						path: [`fields.${index}.value`],
						message: t`${field.label ?? ""} is required`,
					});
					return;
				}

				if (field.value && isAdditionalInfoEmailLabel(field.label ?? "") && !isValidEmail(field.value)) {
					ctx.addIssue({
						code: "custom",
						path: [`fields.${index}.value`],
						message: t`Please enter a valid email address`,
					});
				}
			});
		});
	}, [z, t]);
	return (
		<Form
			schema={schema}
			id={formId}
			initialValues={{
				fields:
					additionalInfo
						?.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
						.map((info) => ({
							permitFieldId: info.id,
							value: initialValues?.fields?.find((field) => field.permitFieldId === info.id)?.value ?? "",
							label: info.label,
							isRequired: info.isRequired,
						})) ?? [],
			}}
			className="flex h-full flex-col justify-between"
			onSubmit={(data) => {
				const fields = data.fields.filter((f) => !!f.value) as AdditionalInfoFormData["fields"];
				onSubmit({
					fields,
				});
			}}
		>
			<Heading>{t`Enter Additional Information`}</Heading>
			<div className="mb-8">
				<Text size="sm">{t`The host has requested guests to provide additional information.`}</Text>
			</div>
			<div className="flex flex-col gap-4">
				{additionalInfo
					?.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
					.map((info, idx) => (
						<div key={info.id}>
							{info.type === AdditionalInfoTypeEnum.Input ? (
								<FormInput
									isRequired={info.isRequired}
									name={`fields.${idx}.value`}
									label={info.label}
									formHelperText={info.description}
									autoComplete="off"
									type={isAdditionalInfoEmailLabel(info.label) ? "email" : "text"}
								/>
							) : (
								<FormSelect
									isRequired={info.isRequired}
									name={`fields.${idx}.value`}
									label={info.label}
									formHelperText={info.description}
								>
									<option value="" />
									{info.additional_field_options?.map((option) => (
										<option key={option.id} value={option.value}>
											{option.value}
										</option>
									))}
								</FormSelect>
							)}
						</div>
					))}
			</div>
		</Form>
	);
};
